import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Col, Row } from "antd";

import PlanCard from "../cro/PlanCard";
import PlansPageFilters from "../cro/PageFilters";
import PlanCardSkeleton from "../cro/PlanCardSkeleton";
import { btnOutline, btnUnderLine } from "../cro/styles/buttonStyles";

import { PlanType } from "../types/planType";
import { getUtilityTypeFromattedString } from "../common";
import { Add, ChevronLeft, ChevronRight, Remove } from "@mui/icons-material";
import { useModal } from "../cro/context/CroModalContext";
import { injectIsFeaturedPlans } from "../utils/injectIsFeaturedPlans";
import { injectCustomDescription } from "../utils/injectCustomDescription";
import { injectSortOrder } from "../utils/injectSortOrder";
import { useTranslation } from "react-i18next";

export const PlanModal = ({
  show,
  data,
  selectProduct,
  isTx,
  utilityList,
  utility,
  loading,
  activeKey,
  zipcode,
  setShowUtilityModal,
  selectedPlans = [],
  backToZipCodePage,
  setActiveKey,
  handleChangeUtility,
  handleSkip,
  skippedCommodity,
  setSkippedCommodity,
}: {
  show: boolean;
  data: any;
  selectProduct: Function;
  isTx: boolean;
  utilityList: any;
  utility: any;
  loading: boolean;
  activeKey: string;
  zipcode: string;
  setShowUtilityModal: Function;
  selectedPlans: any;
  backToZipCodePage: Function;
  setActiveKey: Function;
  handleChangeUtility: Function;
  handleSkip: Function;
  skippedCommodity: boolean;
  setSkippedCommodity: Function;
}) => {
  const navigate = useNavigate();
  const showMoreBtnRef = useRef<HTMLButtonElement>(null);

  const { t, i18n } = useTranslation("cro");

  const currentUtilityDetails =
    utilityList?.[activeKey]?.find(
      (item: any) =>
        item &&
        item.id_utility != null &&
        Number(item.id_utility) === Number(utility?.[activeKey])
    ) || null;

  const [plans, setPlans] = useState<PlanType[] | []>([]);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [injectingFeaturedPlans, setInjectingFeaturedPlans] = useState(false);
  const { hideModal } = useModal();
  const notSelectedUtility = activeKey === "Electric" ? "Gas" : "Electric";
  const stateCode = currentUtilityDetails?.markets?.state_code;

  const utilityName =
    utilityList?.[activeKey]?.find(
      (item: any) =>
        item &&
        item.id_utility != null &&
        Number(item.id_utility) === Number(utility?.[activeKey])
    )?.name || "";

  useEffect(() => {
    async function injectPlanData() {
      setInjectingFeaturedPlans(true);
      if (data && data[activeKey]?.length > 0) {
        const sortedPlans = data[activeKey].sort(
          (a: PlanType, b: PlanType) => (Number(a.Ef_Order) || 99) - (Number(b.Ef_Order) || 99)
        );

        const injectIsFeatured = await injectIsFeaturedPlans(
          sortedPlans,
          utility[activeKey]
        );
        const injectCustomDescriptions = await injectCustomDescription(
          (injectIsFeatured || []) as PlanType[],
          stateCode
        );
        const injectCustomSortOrder = await injectSortOrder(
          injectCustomDescriptions as PlanType[],
          stateCode,
          utility[activeKey]
        );

        // for the translated plans, we replace the plan.ef_plan_details with plan.ef_plan_details_ES
        const translatedPlans = injectCustomSortOrder.map((plan: PlanType) => {
          if (i18n.language === "es") {
            return {
              ...plan,
              ef_plan_details: {
                ...plan.ef_plan_details,
                plan_bullets:
                  plan.ef_plan_details_ES?.plan_bullets ||
                  plan.ef_plan_details?.plan_bullets,
                plan_descriptions:
                  plan.ef_plan_details_ES?.plan_descriptions ||
                  plan.ef_plan_details?.plan_descriptions,
              },
            };
          }
          return plan;
        });

        setPlans(translatedPlans as PlanType[]);
        setInjectingFeaturedPlans(false);
      } else {
        setPlans([]);
        setInjectingFeaturedPlans(false);
      }
    }
    injectPlanData();
  }, [data, activeKey, utility, stateCode, i18n.language]);

  useEffect(() => {
    setShowAllPlans(false); // reset the show plans when the page is opened
    setSkippedCommodity(false); // reset the skipped commodity when the page is opened
    if (!show) return;
    window.scrollTo(0, 0);
    let params = window.location.href.split("?")[1];
    params ? navigate(`/plans?${params}`) : navigate(`/plans`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const getUtilityDetails = (utilityCode: string | number) => {
    if (!utilityList) return null;
    const utilityDetails = Object.keys(utilityList).reduce(
      (acc: Record<string, any>, key) => {
        const foundUtility = utilityList[key].find(
          (item: any) =>
            String(item.id_utility) === String(utilityCode) ||
            String(item.utility_code) === String(utilityCode)
        );
        if (foundUtility) {
          acc[key] = foundUtility;
        }
        return acc;
      },
      {}
    );
    return utilityDetails;
  };

  const switchCommodity = () => {
    setActiveKey(notSelectedUtility);
    // handleChangeUtility(
    //   notSelectedUtility,
    //   utilityList &&
    //     (utilityList[notSelectedUtility]?.find(
    //       (item: any) =>
    //         String(item.id_utility) === String(utility[notSelectedUtility])
    //     )?.id_utility ||
    //       utilityList[notSelectedUtility][0]?.id_utility)
    // );
  };

  const PlanPageHeader = () => {
    return (
      <>
        <Col span={24} style={{ textAlign: "center", marginBottom: 20 }}>
          <Row justify={"center"} align={"middle"}>
            <Col>
              <span>
                {t("Service zip code:")}
                {zipcode && <b>{zipcode}</b>}
                <button
                  onClick={() => {
                    backToZipCodePage();
                  }}
                  style={btnUnderLine}
                >
                  {t("Edit")}
                </button>
              </span>{" "}
            </Col>
            <Col className="d-none d-md-flex">
              <span style={{ padding: "0 .25rem" }}></span> |{" "}
              <span style={{ padding: "0 .25rem" }}></span>
            </Col>
            <Col>
              {t("Utility provider:")}
              {Object.keys(utilityList)?.length === 1 &&
              (utilityList?.Electric?.length === 1 ||
                utilityList?.Gas?.length === 1) ? (
                <>
                  {activeKey === "Electric" && utilityList?.Electric?.length ? (
                    <span className="mx-1" style={{ fontWeight: "bold" }}>
                      {getUtilityDetails(utility["Electric"])?.Electric?.name}
                    </span>
                  ) : activeKey === "Gas" && utilityList?.Gas?.length ? (
                    <span className="mx-1" style={{ fontWeight: "bold" }}>
                      {getUtilityDetails(utility["Gas"])?.Gas?.name}
                    </span>
                  ) : (
                    t("Select Utility")
                  )}
                </>
              ) : (
                <>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : activeKey === "Electric" &&
                    utilityList?.Electric?.length ? (
                    <span className="mx-1" style={{ fontWeight: "bold" }}>
                      {getUtilityDetails(utility["Electric"])?.Electric?.name}
                    </span>
                  ) : activeKey === "Gas" && utilityList?.Gas?.length ? (
                    <span className="mx-1" style={{ fontWeight: "bold" }}>
                      {getUtilityDetails(utility["Gas"])?.Gas?.name}
                    </span>
                  ) : (
                    t("Select Utility")
                  )}
                  <button
                    onClick={() => {
                      if (
                        utilityList?.Electric?.length > 1 ||
                        utilityList?.Gas?.length > 1 ||
                        (utilityList?.Electric?.length > 0 &&
                          utilityList?.Gas?.length > 0)
                      ) {
                        setShowUtilityModal(true);
                      } else {
                        return;
                      }
                    }}
                    style={btnUnderLine}
                  >
                    {t("Edit")}
                  </button>
                </>
              )}
            </Col>
          </Row>

          {/* {plans && plans[0]?.commodity_name && (
            <h1 className="fw-bold m-0 p-0 lg:display-4 my-2">
              Select The Best{" "}
              {getUtilityTypeFromattedString(plans[0].commodity_name)} Plan For
              You
            </h1>
          )} */}
          {plans && plans[0]?.commodity_name === "Electric" ? (
            <h1 className="fw-bold m-0 p-0 lg:display-4 my-2">
              {t("Select The Best Electricity Plan For You")}
            </h1>
          ) : null}
          {plans && plans[0]?.commodity_name === "Gas" ? (
            <h1 className="fw-bold m-0 p-0 lg:display-4 my-2">
              {t("Select The Best Natural Gas Plan For You")}
            </h1>
          ) : null}
        </Col>
        {utilityList &&
          Object.keys(utility)?.length > 1 &&
          utility &&
          utility["Gas"] &&
          utility["Electric"] && (
            <Col span={24}>
              <Row
                justify={skippedCommodity || selectedPlans.length === 1 ? "space-between" : "end"}
                align="middle"
                style={{ marginBottom: "1rem" }}
              >
                {(skippedCommodity || selectedPlans.length === 1) && (
                  <Col
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      type="link"
                      style={{
                        color: "#253f66",
                        margin: "0 0.15rem",
                        padding: 0,
                      }}
                      onClick={() => {
                        setSkippedCommodity(false);
                        switchCommodity();
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div>
                          <ChevronLeft style={{ color: "#253f66" }} />
                        </div>
                        <div>
                          {getUtilityTypeFromattedString(notSelectedUtility) ===
                          "Electricity"
                            ? t("Back to Electricity Plans")
                            : getUtilityTypeFromattedString(
                                notSelectedUtility
                              ) === "Natural Gas"
                            ? t("Back to Natural Gas Plans")
                            : null}
                        </div>
                      </div>
                    </Button>
                  </Col>
                )}
                {!skippedCommodity ? (
                  <Col>
                    <Button
                      type="link"
                      style={{
                        color: "#253f66",
                        margin: "0 0.15rem",
                        padding: 0,
                      }}
                      onClick={() => {
                        setSkippedCommodity(true);
                        if (
                          selectedPlans?.length === 1 ||
                          selectedPlans?.length === 2
                        ) {
                          handleSkip();
                        } else {
                          switchCommodity();
                        }
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div>{t("Skip")}</div>
                        <div>
                          <ChevronRight style={{ color: "#253f66" }} />
                        </div>
                      </div>
                    </Button>
                  </Col>
                ) : null}
              </Row>
            </Col>
          )}
      </>
    );
  };

  if (!show) return null;

  const displayedPlans = plans.slice(0, showAllPlans ? plans.length : 3);

  return (
    <Row gutter={[16, 16]} justify="start" style={{ margin: "2rem 0" }}>
      <Col span={24}>
        <PlanPageHeader />
      </Col>

      <Col span={24}>
        {loading || injectingFeaturedPlans ? (
          <Row gutter={[16, 16]} justify="start" align="bottom">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <Col
                  key={index}
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ marginBottom: 20 }}
                >
                  <PlanCardSkeleton />
                </Col>
              ))}
          </Row>
        ) : displayedPlans.length ? (
          <>
            <Row gutter={[16, 16]} justify="start" align="bottom">
              <Col span={24} style={{ textAlign: "center", zIndex: 99 }}>
                {/* filter component needs to render right after plans are set to prvent infinet loop and other state issues */}
                <PlansPageFilters
                  plans={plans}
                  setPlans={setPlans}
                  commodityName={plans?.length ? plans[0]?.commodity_name : ""}
                />
              </Col>
              <Col span={24}>
                <Row gutter={[40, 40]} justify="start" align="top">
                  {displayedPlans.map((plan) => (
                    <Col
                      key={plan.plan_id}
                      xs={24}
                      sm={12}
                      md={12}
                      lg={8}
                      style={{ marginBottom: 20 }}
                    >
                      <PlanCard
                        isFeatured={plan?.isFeatured}
                        utilityName={utilityName}
                        plan={plan}
                        isTypeVariable={
                          stateCode === "PA" &&
                          plan?.plan_name?.includes("Embrace Green 3")
                        }
                        // below are all the optional props. needed for savings calculation
                        isTx={isTx}
                        isPa={stateCode === "PA"}
                        userZipCode={zipcode}
                        utilityId={String(utility[activeKey])}
                        selectPlan={() => {
                          hideModal();
                          const utility_code =
                            getUtilityDetails(utility[activeKey])?.Electric
                              ?.utility_code ||
                            getUtilityDetails(utility[activeKey])?.Gas
                              ?.utility_code;

                          if (!utility_code) {
                            window.alert("Utility code not found");
                            return;
                          }
                          selectProduct(plan, utility_code);
                          if (
                            Object.keys(utilityList)?.length > 1 &&
                            !skippedCommodity
                          ) {
                            setActiveKey(notSelectedUtility);
                            // handleChangeUtility(
                            //   notSelectedUtility,
                            //   utilityList &&
                            //     (utilityList[notSelectedUtility]?.find(
                            //       (item: any) =>
                            //         String(item.id_utility) ===
                            //         String(utility[notSelectedUtility])
                            //     )?.id_utility ||
                            //       utilityList[notSelectedUtility][0]
                            //         ?.id_utility)
                            // );
                          }
                        }}
                      />
                    </Col>
                  ))}
                </Row>
              </Col>
              {plans.length > 3 && (
                <Col
                  span={24}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Button
                    style={btnOutline}
                    onClick={() => {
                      if (showAllPlans) {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      } else if (showMoreBtnRef.current) {
                        showMoreBtnRef.current.scrollIntoView({
                          behavior: "smooth",
                          block: "nearest",
                        });
                      }
                      setShowAllPlans((prev) => !prev);
                    }}
                  >
                    {showAllPlans ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {t("SHOW LESS PLANS")}
                        <Remove
                          style={{
                            margin: "0 0.25rem",
                            fontSize: "1rem",
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {t("SEE MORE PLANS")}
                        <Add
                          style={{
                            margin: "0 0.25rem",
                            fontSize: "1rem",
                          }}
                        />
                      </div>
                    )}
                  </Button>
                </Col>
              )}
            </Row>
          </>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40vh",
            }}
          >
            <h1 className="fw-bold m-0 p-0 lg:display-4">
              Something went wrong, please try again later or contact us.{" "}
            </h1>
          </div>
        )}
      </Col>
    </Row>
  );
};
